import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Grid,
  Skeleton,
  Button,
} from '@mui/material';
import { ICachedList, useStore } from '@mcn-platform/models';
import Tabs from 'components/Tabs';
import CustomerTrackingData from './CustomerTrackingData';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const itemTypes = [
  { modelName: 'all', label: 'All' },
  { modelName: 'course', label: 'Courses' },
  { modelName: 'program', label: 'Programs' },
  { modelName: 'microcast', label: 'Microcasts' },
];

const DateFormatter = ({ value }: any) => (value ? new Date(value).toDateString() : '');

const CustomerTracking = ({ customer }:any) => {
  const store = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tracking');
  const trackingTab = rawTab ?? 'itemStarted';
  const [selectedItemType, setSelectedItemType] = useState('all');
  const [progressList, setProgressList] = useState<ICachedList | undefined>(undefined);
  const selectTrackingTab = (newTab:string) => {
    setSearchParams({
      tracking: newTab,
    });
  };

  const {
    createdAt,
    name,
    email,
  } = customer;

  const handleItemChange = (e: any) => {
    setSelectedItemType(e.target.value as string);
  };

  const canExport = (trackingTab === 'itemStarted' || trackingTab === 'itemCompleted');

  const handleExportClick = useCallback(() => {
    if (!canExport || !progressList?.items?.length) {
      return;
    }

    const exportableItems = (progressList?.items || []).map((item) => item);
    const sortedItems = exportableItems.sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0));
    const exportData = sortedItems.map((item) => {
      const tracking = typeof item.tracking === 'string' ? JSON.parse(item.tracking) : item.tracking;
      let itemType = '-';
      if (item.program) itemType = 'Program';
      else if (item.microcast) itemType = 'Microcast';
      else if (item.course) itemType = 'Course';

      return {
        Type: itemType,
        'Item Name': item?.course?.name || item?.program?.name || item?.microcast?.name || '-',
        'Date Started': item.startedDate || '',
        'Last Active Date': item.lastActiveDate || '',
        'Date Completed': item.completeDate || '',
        Progress: item.progress ?? 0,
        'Time Watched (min)': tracking?.totalTime ? Math.floor(tracking.totalTime / 60) : 0,
      };
    }).filter((row) => Object.values(row).some(
      (value) => value !== null
        && value !== undefined
        && value !== ''
        && value !== 'N/A'
        && value !== '-',
    ));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Progress Data');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
    saveAs(blob, `customer_progress_${trackingTab || 'data'}.xlsx`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressList, trackingTab]);

  useEffect(() => {
    const loadItems = async () => {
      const newList = store.getCachedList('itemProgress', {
        customerID: { eq: customer.id },
        ...selectedItemType !== 'all' && { [`${selectedItemType}ID`]: { exists: true } },
        ...trackingTab === 'itemCompleted' && { progress: { eq: 100 } },
        ...trackingTab === 'itemStarted' && { startedDate: { exists: true } },
        ...trackingTab === 'certifications' && { certifications: { exists: true } },
      });
      newList.setRequestedCount(1000);
      // store.fetchCachedList(progressList);
      setProgressList(newList);
      console.log('we got', newList?.items?.length);
    };
    setProgressList(undefined);
    loadItems();
  }, [
    selectedItemType,
    store,
    customer?.id,
    trackingTab,
  ]);

  return (
    <Box>
      <Grid container sx={{ p: 1 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">
            { `Customer Analytics for: ${name === '' ? email : name}` }
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">
            { `Account activation date: ${DateFormatter(createdAt)}` }
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ p: 1 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">
            Item Analytics
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Select
            id="model-type"
            value={selectedItemType}
            onChange={handleItemChange}
            sx={{ minWidth: '150px', height: '30px' }}
          >
            {itemTypes.map((i) => (
              <MenuItem key={i.modelName} value={i.modelName}>{i.label}</MenuItem>
            ))}
          </Select>
          {canExport && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleExportClick}
              disabled={progressList?.syncState !== 'loaded'}
            >
              Export
            </Button>
          )}
        </Grid>
      </Grid>
      {progressList?.syncState === 'loaded' ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: '100%',
        }}
        >
          <Tabs
            tabs={[{
              key: 'itemStarted',
              label: 'Items Started',
              content: <CustomerTrackingData progressList={progressList} customer={customer} />,
            },
            {
              key: 'itemCompleted',
              label: 'Items Completed',
              content: <CustomerTrackingData progressList={progressList} customer={customer} />,
            },
            {
              key: 'itemPurchased',
              label: 'Item Purchased',
              content: <CustomerTrackingData progressList={progressList} customer={customer} />,
            },
            {
              key: 'certifications',
              label: 'Certifications',
              content: (
                <CustomerTrackingData
                  progressList={progressList}
                  isCertification
                  customer={customer}
                />
              ),
            },
            ]}
            selected={trackingTab || 'itemStarted'}
            setSelected={selectTrackingTab}
          />
        </Box>
      ) : (
        <Skeleton variant="rectangular" height={400} />
      )}
    </Box>
  );
};

export default observer(CustomerTracking);
